import { Plus } from 'lucide-react';
import { Link } from 'react-router';
import { useBonsCommande } from '@/api/hooks';
import { LIBELLE_CATEGORIE_ACHAT, LIBELLE_STATUT, type StatutDocument } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, EtapeBadge, PageHeader, StatutBadge, Vide } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFiltresListe } from '@/lib/filtres-liste';
import { dateFr, montant } from '@/lib/utils';

const STATUTS = Object.keys(LIBELLE_STATUT) as StatutDocument[];

export function BcListe() {
  const session = useSession();
  const u = session!.utilisateur;
  const acheteur = u.profils.some((p) => p === 'CHARGE_ACHATS' || p === 'RESPONSABLE_ACHATS' || p === 'CELLULE_MARCHES');
  const valideur = u.etapes.some((e) => e === 'RESPONSABLE_ACHATS' || e === 'CELLULE_MARCHES' || e === 'DIRECTEUR_GENERAL');
  const { onglet, statut, setOnglet, setStatut } = useFiltresListe(valideur ? (['a-traiter', 'tous'] as const) : (['tous'] as const), valideur ? 'a-traiter' : 'tous', 'tous');
  const { data, isLoading, error } = useBonsCommande(onglet === 'a-traiter' ? { aTraiter: true } : statut === 'TOUS' ? {} : { statut });

  return (
    <div>
      <PageHeader
        titre="Achats · bons de commande"
        sousTitre="Émis par les achats sur réquisition, contrôlés par le responsable des achats, validés par la Cellule des marchés, autorisés par le Directeur Général."
        actions={
          acheteur && (
            <Button asChild>
              <Link to="/achats/nouveau">
                <Plus className="size-4" /> Nouveau bon de commande
              </Link>
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={onglet} onValueChange={(v) => setOnglet(v as 'a-traiter' | 'tous')}>
          <TabsList>
            {valideur && <TabsTrigger value="a-traiter">À traiter</TabsTrigger>}
            <TabsTrigger value="tous">{acheteur || u.habilitations.some((h) => h.structureId === null) ? 'Tous les bons' : 'Bons de ma structure'}</TabsTrigger>
          </TabsList>
        </Tabs>
        {onglet === 'tous' && (
          <Select value={statut} onValueChange={(v) => setStatut(v as StatutDocument | 'TOUS')}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TOUS">Tous les statuts</SelectItem>
              {STATUTS.map((s) => (
                <SelectItem key={s} value={s}>
                  {LIBELLE_STATUT[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message={onglet === 'a-traiter' ? 'Aucun bon de commande en attente de votre décision.' : 'Aucun bon de commande.'} />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Objet</TableHead>
                <TableHead className="hidden md:table-cell">Fournisseur</TableHead>
                <TableHead className="hidden lg:table-cell">Catégorie</TableHead>
                <TableHead className="hidden md:table-cell">Structure</TableHead>
                <TableHead className="text-right">Montant (USD)</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="hidden sm:table-cell">Mis à jour</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <Link to={`/achats/${b.id}`} className="font-medium text-primary hover:underline">
                      {b.numero ?? 'Brouillon'}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-[32ch] truncate">{b.objet}</TableCell>
                  <TableCell className="hidden md:table-cell">{b.fournisseur}</TableCell>
                  <TableCell className="hidden lg:table-cell">{LIBELLE_CATEGORIE_ACHAT[b.categorie]}</TableCell>
                  <TableCell className="hidden md:table-cell">{b.structureCode}</TableCell>
                  <TableCell className="num text-right">{montant(b.montantUsd, true)}</TableCell>
                  <TableCell className="space-x-1">
                    <StatutBadge statut={b.statut} />
                    {b.statut === 'SOUMIS' && <EtapeBadge etape={b.etapeCourante} />}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{dateFr(b.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
